import React from "react";
import FormLabel from "./FormLabel";
import { selectionClass, tabSizeClass } from "./tabStyles";

export const FORM_CARD_CLASS = "p-5 space-y-3";
export const FORM_STYLE_CARD_CLASS = "p-5 space-y-4";
/** Public host/school listing surfaces — quiet white cards on canvas. */
export const PUBLIC_LISTING_CARD_CLASS = `${FORM_STYLE_CARD_CLASS}`;

export function FormValidationAlert({ count }) {
  if (!count) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className="rounded-card border border-red-200 bg-red-50 px-4 py-3 font-ui text-[14px] font-medium text-red-800 leading-snug"
      data-testid="form-validation-summary"
    >
      Fix {count} highlighted field{count === 1 ? "" : "s"} below.
    </div>
  );
}

export function SectionTitle({ children, className = "", icon: Icon }) {
  return (
    <h2 className={`font-ui font-semibold text-[15px] leading-snug text-ink flex items-center gap-2 ${className}`}>
      {Icon ? <Icon className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden /> : null}
      {children}
    </h2>
  );
}

export function FieldHint({ children, className = "", as: Tag = "p", ...rest }) {
  return (
    <Tag className={`font-ui text-[13px] font-medium text-ink/80 leading-relaxed ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

export function DiscoverCheckboxRow({ children, className = "", ...rest }) {
  return (
    <label className={`flex items-start gap-3 cursor-pointer py-3 ${className}`} {...rest}>
      {children}
    </label>
  );
}

/** Same selected fill + size as product TabButton (default). */
export function choiceToggleClass(active, disabled = false) {
  return `${tabSizeClass("default")} ${selectionClass(active, { disabled })}`;
}

/** Flat multi/single-select pill — readable register (no sticker press). */
export function ReadablePillToggle({ active, children, onClick, disabled = false, className = "", ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`${choiceToggleClass(active, disabled)} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Flat metadata chip for workflow surfaces (lists, stats). */
export function ReadableBadge({ children, className = "", ...rest }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-btn border border-subtle bg-surface-muted px-2.5 py-1 font-ui font-medium text-[12px] text-ink ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
}

/** Segmented control for pricing, access, recurring, etc. */
export function ChoiceToggleGroup({
  options,
  value,
  onChange,
  isDisabled = () => false,
  getDescribedBy = () => undefined,
  role = "button",
  testIdPrefix,
}) {
  return (
    <div className="flex gap-2 flex-wrap items-center">
      {options.map((o) => {
        const disabled = isDisabled(o.k);
        const active = value === o.k;
        const buttonProps = {
          type: "button",
          onClick: () => !disabled && onChange(o.k),
          disabled,
          className: choiceToggleClass(active, disabled),
          "data-testid": testIdPrefix ? `${testIdPrefix}-${o.k}-btn` : undefined,
          "aria-describedby": getDescribedBy(o.k),
        };
        if (role === "radio") {
          return (
            <button key={o.k} {...buttonProps} role="radio" aria-checked={active}>
              {o.label}
            </button>
          );
        }
        return (
          <button key={o.k} {...buttonProps} aria-pressed={active}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function CheckboxField({ id, checked, onChange, title, hint, testId, invalid, describedBy, disabled }) {
  return (
    <div className={`flex items-start gap-3 ${hint ? "py-3" : "py-1.5"}`}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="w-5 h-5 accent-plum mt-0.5 shrink-0 disabled:opacity-50"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-testid={testId}
      />
      <label htmlFor={id} className={`min-w-0 ${disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
        <p className="font-ui font-medium text-[15px]">{title}</p>
        {hint ? <FieldHint className="text-[12px] sm:text-[13px]">{hint}</FieldHint> : null}
      </label>
    </div>
  );
}

export function LabeledField({ id, label, required, error, children }) {
  return (
    <div className="block">
      <FormLabel as="label" htmlFor={id} className="block mb-2">
        {label}
        {required ? " *" : ""}
      </FormLabel>
      {children}
    </div>
  );
}
